import { cn } from "@app/lib/utils";
import { HeroSectionBackground } from "@modules/sectors/components";
import { HeroSectionBG } from "@modules/shared/components";
import { getCurrentLocaleCode, trans } from "@mongez/localization";
import { Breadcrumb, type BreadcrumbItem } from "./Breadcrumb";

type PageHeaderProps = {
  title?: string;
  translationKey?: string; // For translations like trans("aboutUs")
  breadcrumbItems: BreadcrumbItem[];
  image?: string;
  imageAlt?: string;
  children?: React.ReactNode; // Normal React children instead of HTML string
  contentClassName?: string; // Custom styling for content
  BackgroundComponent?: React.ComponentType; // For custom background components
  className?: string;
  SideComponent?: React.ComponentType; // Optional side component
  titleClassName?: string;
  imageClassName?: string;
  color?: string;
};

export function PageHeader({
  title,
  translationKey,
  breadcrumbItems,
  image,
  imageAlt = "Page header image",
  SideComponent,
  children,
  contentClassName = "space-y-4 leading-relaxed text-gray-200",
  BackgroundComponent = HeroSectionBG,
  titleClassName,
  className = "",
  imageClassName,
  color,
}: PageHeaderProps) {
  const currentLocale = getCurrentLocaleCode();

  const displayTitle = translationKey ? trans(translationKey) : title;

  return (
    <section
      className={cn(
        "relative min-h-max w-full overflow-hidden bg-primary pb-24 max-md:pb-10",
        className,
      )}
      style={{
        background: color,
      }}
    >
      {color && (
        <span className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
      )}
      <div
        className={cn(
          "absolute left-0 top-0 h-auto w-full",
          color && "z-20 h-full",
        )}
      >
        {color ? (
          <HeroSectionBackground />
        ) : (
          BackgroundComponent && <BackgroundComponent />
        )}
      </div>

      <div
        className={cn(
          "relative z-10 gap-6 overflow-hidden pt-10 sm:gap-8 sm:pt-20 md:gap-12 md:pt-[94px]",
          currentLocale === "en" ? "container-left" : "container-right",
        )}
      >
        <div className="space-y-4 text-white sm:space-y-6 md:space-y-8">
          <div className="grid w-full grid-cols-1 gap-8 sm:gap-6 lg:grid-cols-12 lg:gap-10">
            <div
              className={cn(
                "w-full lg:col-span-4",
                !image && !SideComponent && "lg:col-span-12",
              )}
            >
              <Breadcrumb items={breadcrumbItems} />
              <h1
                className={cn(
                  "mt-3 text-3xl font-semibold text-bright-orange sm:mt-4 sm:text-4xl md:mt-6 md:text-5xl  xl:text-[96px]",
                  color && "text-white",
                  titleClassName,
                )}
              >
                {displayTitle}
              </h1>
            </div>

            <div
              className={cn(
                "lg:col-span-8",
                !image && !SideComponent && "hidden",
              )}
            >
              {SideComponent !== undefined ? (
                <SideComponent />
              ) : image ? (
                <img
                  src={image}
                  alt={imageAlt}
                  className={cn(
                    "w-full rounded-[40px] ltr:rounded-r-none rtl:rounded-l-none",
                    imageClassName,
                  )}
                />
              ) : null}
            </div>
          </div>

          <div className="flex gap-0 p-0 sm:gap-4 sm:px-4 sm:pt-4 md:gap-8 md:px-0 md:pt-8 2xl:gap-[137px]">
            {/* Normal React Children */}
            {children && <div className={cn(contentClassName)}>{children}</div>}
          </div>
        </div>
      </div>
    </section>
  );
}
